import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

const VisionSection = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="bg-[#1A0A0A] py-20 lg:py-32 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#C8102E] rounded-full blur-[200px]"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div 
            className="text-[#C8102E] text-base md:text-lg tracking-[0.2em] font-medium uppercase mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {data.label}
          </motion.div>
          
          <motion.h2
            className="text-white font-bold text-5xl md:text-6xl lg:text-7xl leading-tight mb-8 whitespace-pre-line"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {data.headline}
          </motion.h2>

          <motion.p
            className="text-[#F0EBE3] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-16"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {data.subtext}
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid md:grid-cols-3 gap-8"
        >
          {data.stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.15 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="text-center p-8 bg-[#0A0A0A]/50 rounded-lg backdrop-blur-sm border border-[#C8102E]/20 hover:border-[#C8102E]/50 transition-all duration-300"
            >
              <CountUpStat 
                value={stat.value} 
                isInView={isInView}
                className="text-[#C8102E] text-3xl md:text-4xl font-bold mb-3"
                style={{ fontFamily: "'Ubuntu', sans-serif" }}
              />
              <div
                className="text-[#F0EBE3]/70 text-sm"
                style={{ fontFamily: "'Ubuntu', sans-serif" }}
              >
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const CountUpStat = ({ value, isInView, className, style }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const isNumeric = /^\d+/.test(value);

  useEffect(() => {
    if (!isInView || !isNumeric) return;

    const numericPart = value.match(/^\d+/)?.[0];
    const suffix = value.replace(/^\d+/, '');
    const target = parseInt(numericPart, 10);
    const duration = 2000;
    const steps = 50;
    const increment = target / steps;
    const stepDuration = duration / steps;

    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current) + suffix);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isInView, value, isNumeric]);

  return <div className={className} style={style}>{displayValue}</div>;
};

export default VisionSection;
